import type {
  AuthenticationResponseJSON,
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
  RegistrationResponseJSON,
} from '@simplewebauthn/browser';
import {z} from 'zod';

import {IsoUtcDateTimeSchema, UuidV4Schema} from './common.js';

export type {
  AuthenticationResponseJSON,
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
  RegistrationResponseJSON,
} from '@simplewebauthn/browser';

const Base64UrlSchema = z
  .string()
  .min(1)
  .regex(/^[A-Za-z0-9_-]+$/u);
const PublicKeyCredentialTypeSchema = z.literal('public-key');
const UserVerificationSchema = z.enum(['discouraged', 'preferred', 'required']);
const AuthenticatorAttachmentSchema = z.enum(['cross-platform', 'platform']);
const AuthenticatorTransportSchema = z.enum([
  'ble',
  'cable',
  'hybrid',
  'internal',
  'nfc',
  'smart-card',
  'usb',
]);
const ClientExtensionResultsSchema = z.looseObject({});

const PublicKeyCredentialDescriptorSchema = z.strictObject({
  id: Base64UrlSchema,
  type: PublicKeyCredentialTypeSchema,
  transports: z.array(AuthenticatorTransportSchema).optional(),
});

const AuthenticatorSelectionSchema = z.strictObject({
  authenticatorAttachment: AuthenticatorAttachmentSchema.optional(),
  residentKey: z.enum(['discouraged', 'preferred', 'required']).optional(),
  requireResidentKey: z.boolean().optional(),
  userVerification: UserVerificationSchema.optional(),
});

const PublicKeyCredentialCreationOptionsJSONShape = z.strictObject({
  rp: z.strictObject({
    id: z.string().min(1).optional(),
    name: z.string().min(1),
  }),
  user: z.strictObject({
    id: z.string().min(1),
    name: z.string().min(1),
    displayName: z.string().min(1),
  }),
  challenge: Base64UrlSchema,
  pubKeyCredParams: z
    .array(
      z.strictObject({
        alg: z.number().int(),
        type: PublicKeyCredentialTypeSchema,
      }),
    )
    .min(1),
  timeout: z.number().int().positive().optional(),
  excludeCredentials: z.array(PublicKeyCredentialDescriptorSchema).optional(),
  authenticatorSelection: AuthenticatorSelectionSchema.optional(),
  hints: z
    .array(z.enum(['client-device', 'hybrid', 'security-key']))
    .optional(),
  attestation: z.enum(['direct', 'enterprise', 'indirect', 'none']).optional(),
  attestationFormats: z
    .array(
      z.enum([
        'android-key',
        'android-safetynet',
        'apple',
        'fido-u2f',
        'none',
        'packed',
        'tpm',
      ]),
    )
    .optional(),
  extensions: z.looseObject({}).optional(),
});

export const PublicKeyCredentialCreationOptionsJSONSchema =
  PublicKeyCredentialCreationOptionsJSONShape.transform(
    (options): PublicKeyCredentialCreationOptionsJSON =>
      options as PublicKeyCredentialCreationOptionsJSON,
  );

const PublicKeyCredentialRequestOptionsJSONShape = z.strictObject({
  challenge: Base64UrlSchema,
  timeout: z.number().int().positive().optional(),
  rpId: z.string().min(1).optional(),
  allowCredentials: z.array(PublicKeyCredentialDescriptorSchema).optional(),
  userVerification: UserVerificationSchema.optional(),
  hints: z
    .array(z.enum(['client-device', 'hybrid', 'security-key']))
    .optional(),
  extensions: z.looseObject({}).optional(),
});

export const PublicKeyCredentialRequestOptionsJSONSchema =
  PublicKeyCredentialRequestOptionsJSONShape.transform(
    (options): PublicKeyCredentialRequestOptionsJSON =>
      options as PublicKeyCredentialRequestOptionsJSON,
  );

const RegistrationResponseJSONShape = z.strictObject({
  id: Base64UrlSchema,
  rawId: Base64UrlSchema,
  response: z.strictObject({
    clientDataJSON: Base64UrlSchema,
    attestationObject: Base64UrlSchema,
    authenticatorData: Base64UrlSchema.optional(),
    transports: z.array(AuthenticatorTransportSchema).optional(),
    publicKeyAlgorithm: z.number().int().optional(),
    publicKey: Base64UrlSchema.optional(),
  }),
  authenticatorAttachment: AuthenticatorAttachmentSchema.optional(),
  clientExtensionResults: ClientExtensionResultsSchema,
  type: PublicKeyCredentialTypeSchema,
});

export const RegistrationResponseJSONSchema =
  RegistrationResponseJSONShape.transform(
    (response): RegistrationResponseJSON =>
      response as RegistrationResponseJSON,
  );

const AuthenticationResponseJSONShape = z.strictObject({
  id: Base64UrlSchema,
  rawId: Base64UrlSchema,
  response: z.strictObject({
    clientDataJSON: Base64UrlSchema,
    authenticatorData: Base64UrlSchema,
    signature: Base64UrlSchema,
    userHandle: Base64UrlSchema.optional(),
  }),
  authenticatorAttachment: AuthenticatorAttachmentSchema.optional(),
  clientExtensionResults: ClientExtensionResultsSchema,
  type: PublicKeyCredentialTypeSchema,
});

export const AuthenticationResponseJSONSchema =
  AuthenticationResponseJSONShape.transform(
    (response): AuthenticationResponseJSON =>
      response as AuthenticationResponseJSON,
  );

export const OwnerUserSchema = z.strictObject({
  id: UuidV4Schema,
  email: z.string().trim().toLowerCase().pipe(z.email()),
  role: z.literal('owner'),
  createdAt: IsoUtcDateTimeSchema,
});

export type OwnerUser = z.infer<typeof OwnerUserSchema>;
export type OwnerUserDto = OwnerUser;

export const AuthSessionResponseSchema = z.strictObject({
  user: OwnerUserSchema,
  expiresAt: IsoUtcDateTimeSchema,
  idleExpiresAt: IsoUtcDateTimeSchema,
});
export type AuthSessionResponse = z.infer<typeof AuthSessionResponseSchema>;

/**
 * O usuário como ele chega nas respostas de sessão da API.
 *
 * `OwnerUserSchema` exige e-mail e data de criação, mas `login/verify` devolve
 * só `{id, role}` (`auth.ts` da API, rota de login). Um schema que exigisse o
 * usuário completo recusaria a resposta real do login. Os campos ausentes ficam
 * opcionais, e não anuláveis: ausência aqui é forma da resposta, não medição.
 */
export const SessionUserSchema = z.strictObject({
  id: UuidV4Schema,
  role: z.literal('owner'),
  email: z.string().trim().toLowerCase().pipe(z.email()).optional(),
  createdAt: IsoUtcDateTimeSchema.optional(),
});
export type SessionUser = z.infer<typeof SessionUserSchema>;

/**
 * O envelope que `bootstrap/verify`, `login/verify` e `session/refresh`
 * realmente devolvem: além do usuário e dos vencimentos, o token CSRF da
 * sessão recém-emitida. `AuthSessionResponseSchema` acima não carrega o token
 * e por isso não serve para validar essas respostas; ele fica como está para
 * não mexer no que já o usa.
 */
export const IssuedSessionResponseSchema = z.strictObject({
  user: SessionUserSchema,
  expiresAt: IsoUtcDateTimeSchema,
  idleExpiresAt: IsoUtcDateTimeSchema,
  csrfToken: z.string().min(1),
});
export type IssuedSessionResponse = z.infer<typeof IssuedSessionResponseSchema>;

export const ReauthResponseSchema = z.strictObject({
  reauthenticatedUntil: IsoUtcDateTimeSchema,
});
export type ReauthResponse = z.infer<typeof ReauthResponseSchema>;

export const CsrfResponseSchema = z.strictObject({
  csrfToken: z.string().min(1),
  expiresAt: IsoUtcDateTimeSchema,
});
export type CsrfResponse = z.infer<typeof CsrfResponseSchema>;

export const BootstrapOptionsRequestSchema = z.strictObject({
  enrollmentToken: z.string().min(1),
});
export type BootstrapOptionsRequest = z.infer<
  typeof BootstrapOptionsRequestSchema
>;

export const LoginOptionsRequestSchema = z.strictObject({
  email: z.string().trim().toLowerCase().pipe(z.email()),
});
export type LoginOptionsRequest = z.infer<typeof LoginOptionsRequestSchema>;

const RegistrationOptionsResponseSchema = z.strictObject({
  ceremonyId: UuidV4Schema,
  ceremony: z.literal('registration'),
  options: PublicKeyCredentialCreationOptionsJSONSchema,
  expiresAt: IsoUtcDateTimeSchema,
});

const AuthenticationOptionsResponseSchema = z.strictObject({
  ceremonyId: UuidV4Schema,
  ceremony: z.enum(['authentication', 'reauthentication']),
  options: PublicKeyCredentialRequestOptionsJSONSchema,
  expiresAt: IsoUtcDateTimeSchema,
});

export const PasskeyOptionsResponseSchema = z.discriminatedUnion('ceremony', [
  RegistrationOptionsResponseSchema,
  AuthenticationOptionsResponseSchema,
]);
export type PasskeyOptionsResponse = z.infer<
  typeof PasskeyOptionsResponseSchema
>;

export const BootstrapVerifyRequestSchema = z.strictObject({
  ceremonyId: UuidV4Schema,
  enrollmentToken: z.string().min(1),
  response: RegistrationResponseJSONSchema,
});
export type BootstrapVerifyRequest = z.infer<
  typeof BootstrapVerifyRequestSchema
>;

export const LoginVerifyRequestSchema = z.strictObject({
  ceremonyId: UuidV4Schema,
  response: AuthenticationResponseJSONSchema,
});
export type LoginVerifyRequest = z.infer<typeof LoginVerifyRequestSchema>;

export const ReauthVerifyRequestSchema = z.strictObject({
  ceremonyId: UuidV4Schema,
  response: AuthenticationResponseJSONSchema,
});
export type ReauthVerifyRequest = z.infer<typeof ReauthVerifyRequestSchema>;
